
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const ResetPassword = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  // Extract hash parameters from URL and initialize session
  useEffect(() => {
    const initializeRecoverySession = async () => {
      try {
        // Get the URL hash (without the # character)
        const hashFragment = window.location.hash.substring(1);
        console.log("URL hash fragment:", hashFragment);
        
        if (!hashFragment) {
          console.log("No hash fragment in URL");
          
          // If no hash, check if we already have a session
          const { data: sessionData } = await supabase.auth.getSession();
          
          if (!sessionData.session) {
            console.log("No active session found");
            setError("Invalid or expired password reset link. Please request a new one.");
            toast({
              title: "Invalid Link",
              description: "Your password reset link is invalid or has expired. Please request a new one.",
              variant: "destructive",
            });
            setTimeout(() => navigate("/login"), 3000);
            return;
          }
          
          console.log("Existing session found:", sessionData.session.user.id);
          setInitialized(true);
          return;
        }
        
        // Parse the hash fragment as a URLSearchParams object
        const params = new URLSearchParams(hashFragment);
        
        // Extract tokens
        const accessToken = params.get("access_token");
        const refreshToken = params.get("refresh_token");
        const type = params.get("type");
        
        console.log("Hash params:", { 
          accessToken: !!accessToken, 
          refreshToken: !!refreshToken, 
          type 
        });
        
        // Validate that this is a recovery flow
        if (!accessToken || type !== "recovery") {
          console.log("Invalid tokens or not a recovery flow");
          setError("Invalid password reset link. Please request a new one.");
          toast({
            title: "Invalid Link",
            description: "Your password reset link is invalid. Please request a new one.",
            variant: "destructive",
          });
          setTimeout(() => navigate("/login"), 3000);
          return;
        }
        
        // Set the session with the tokens from the URL
        const { data, error: sessionError } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken || "",
        });
        
        if (sessionError) {
          console.error("Error setting session:", sessionError);
          setError("Your password reset link has expired. Please request a new one.");
          toast({
            title: "Link Expired",
            description: "Your password reset link has expired. Please request a new one.",
            variant: "destructive",
          });
          setTimeout(() => navigate("/login"), 3000);
          return;
        }
        
        console.log("Session set successfully:", data.session?.user.id);
        setInitialized(true);
        
        // Clear the hash from the URL without refreshing the page
        // This prevents tokens from staying in the browser history
        window.history.replaceState(null, document.title, window.location.pathname);
        
      } catch (err) {
        console.error("Error in recovery session initialization:", err);
        setError("An error occurred. Please try again or request a new password reset link.");
        toast({
          title: "Error",
          description: "An error occurred processing your password reset. Please try again.",
          variant: "destructive",
        });
      }
    };
    
    if (!initialized) {
      initializeRecoverySession();
    }
  }, [navigate, toast, initialized]);

  const handleResetPassword = async () => {
    setError(null);
    
    if (password.length < 6) {
      setError("Password must be at least 6 characters long");
      return;
    }
    
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    
    setLoading(true);
    
    try {
      console.log("Attempting to update password");
      
      const { data, error } = await supabase.auth.updateUser({
        password: password
      });
      
      if (error) {
        console.error("Error updating password:", error);
        throw error;
      }
      
      console.log("Password updated successfully for user:", data.user?.id);
      setSuccess(true);
      
      // Sign out after password reset
      await supabase.auth.signOut();
      
      toast({
        title: "Password Reset Successful",
        description: "Your password has been reset successfully. You can now log in with your new password.",
      });
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate("/login");
      }, 3000);
      
    } catch (error) {
      console.error("Password reset error:", error);
      setError(error.message || "An error occurred while resetting your password");
      
      toast({
        title: "Error",
        description: error.message || "Failed to reset password. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!initialized && !error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">
        <div className="text-center mb-8">
          <img 
            src="/lovable-uploads/d62a4e07-f701-4335-84a0-c9be341a0477.png" 
            alt="Bright Stages" 
            className="h-24 md:h-28 lg:h-32 mx-auto mb-6"
          />
        </div>
        <Card className="w-full max-w-md mx-auto">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center justify-center p-4">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
              <p className="mt-4 text-gray-600">Verifying your reset link...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">
      <div className="text-center mb-8 w-full max-w-md mx-auto">
        <img 
          src="/lovable-uploads/d62a4e07-f701-4335-84a0-c9be341a0477.png" 
          alt="Bright Stages" 
          className="h-24 md:h-28 lg:h-32 mx-auto mb-6 transition-all duration-300 hover:scale-105"
        />
        <p className="text-gray-600 text-lg">Reset Your Password</p>
      </div>
      
      <Card className="w-full max-w-md mx-auto">
        <CardHeader>
          <CardTitle className="text-2xl font-heading text-center">
            Create New Password
          </CardTitle>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          {success ? (
            <Alert className="mb-4 bg-green-50 border-green-200">
              <AlertDescription className="text-green-800">
                Password reset successful! Redirecting you to login...
              </AlertDescription>
            </Alert>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="password" className="text-sm font-medium">
                  New Password
                </label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your new password"
                  disabled={loading}
                />
                <p className="text-xs text-gray-500">
                  Password must be at least 6 characters long
                </p>
              </div>
              
              <div className="space-y-2">
                <label htmlFor="confirm-password" className="text-sm font-medium">
                  Confirm New Password
                </label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your new password"
                  disabled={loading}
                />
              </div>
              
              <Button 
                onClick={handleResetPassword} 
                className="w-full" 
                disabled={loading || !initialized}
              >
                {loading ? "Resetting Password..." : "Reset Password"}
              </Button>
              
              <div className="text-center mt-4">
                <Button 
                  variant="link" 
                  onClick={() => navigate("/login")}
                  className="text-sm"
                >
                  Return to Login
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
